import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentPetBreedingApplyLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatResidentPetBreedingApplyForm from './resident/NatResidentPetBreedingApplyForm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {NatPutPetBreedingApplyRequest, NatPutPetBreedingApplyResponse} from '../repository/NatPutPetBreedingApply'
import {ImageInformation} from '../../../../components/shared/forms/resident/PPImageInputWithPreviewForResident'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentPetBreedingApplyLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    petType: Nullable<string>
    petBreed: Nullable<string>
    havePetStartMonth: Nullable<string>
    dogLicenseImage: Nullable<ImageInformation>
    rabiesImage: Nullable<ImageInformation>
    isLoadCompleted: boolean
}

class NatResidentPetBreedingApplyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            petType: null,
            petBreed: null,
            havePetStartMonth: null,
            dogLicenseImage: null,
            rabiesImage: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultPetType = applyInfo?.petType ?? null
        let defaultPetBreed = applyInfo?.petBreed ?? null
        let defaultHavePetStartMonth = applyInfo?.havePetStartMonth ?? null
        let defaultDogLicense = applyInfo?.healthCenterDogLicense ?? null
        let defaultRabies = applyInfo?.rabiesPreventionInjectionCertification ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const petBreedingDetail =
                    await this.props.asyncCallFunction(applicationRepo.getPetBreedingForResident.bind(
                            applicationRepo, condominiumCd, applyId),
                        (httpErrorOrNetworkError) => {
                            eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        },
                    )
                if (petBreedingDetail?.details) {
                    defaultPetType = petBreedingDetail.details.petType
                    defaultPetBreed = petBreedingDetail.details.petBreed
                    defaultHavePetStartMonth = petBreedingDetail.details.havePetStartMonth
                    defaultDogLicense = {
                        fileName: null,
                        fileData: null,
                        fileNumber: petBreedingDetail.details.healthCenterDogLicense.fileNumber,
                        path: petBreedingDetail.details.healthCenterDogLicense.path,
                    }
                    defaultRabies = {
                        fileName: null,
                        fileData: null,
                        fileNumber: petBreedingDetail.details.rabiesPreventionInjectionCertification.fileNumber,
                        path: petBreedingDetail.details.rabiesPreventionInjectionCertification.path,
                    }
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }
        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            petType: defaultPetType,
            petBreed: defaultPetBreed,
            havePetStartMonth: defaultHavePetStartMonth,
            dogLicenseImage: defaultDogLicense,
            rabiesImage: defaultRabies,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutPetBreedingApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutPetBreedingApplyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyPetBreedingApply.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentPetBreedingApplyLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        petType: request.petType,
                        petBreed: request.petBreed,
                        havePetStartMonth: request.havePetStartMonth,
                        healthCenterDogLicense: request.healthCenterDogLicense,
                        rabiesPreventionInjectionCertification: request.rabiesPreventionInjectionCertification,
                    },
                    applyId: this.props.location.state.applyId,
                }
                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/petBreeding/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/petBreeding/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/petBreeding/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    ペット飼育申請
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentPetBreedingApplyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentPetBreedingApplyInputPage)
